import React, { useState } from 'react';
import { Link } from 'react-router-dom';
import { Button } from '../components/ui/button';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '../components/ui/card';
import { Input } from '../components/ui/input';
import { Textarea } from '../components/ui/textarea';
import { Label } from '../components/ui/label';
import { Phone, Mail, Briefcase, Award, Users, TrendingUp, Upload, Loader2 } from 'lucide-react';
import { toast } from '../hooks/use-toast';
import axios from 'axios';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;

const Careers = () => {
  const [formData, setFormData] = useState({
    name: '',
    email: '',
    phone: '',
    position: '',
    experience: '',
    coverLetter: '',
    resume: null
  });
  
  const [isSubmitting, setIsSubmitting] = useState(false);

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    // Validate resume
    if (!formData.resume) {
      toast({
        title: "Error",
        description: "Please upload your resume/CV",
        variant: "destructive"
      });
      return;
    }
    
    setIsSubmitting(true);
    
    try {
      // Prepare form data
      const submitData = new FormData();
      submitData.append('name', formData.name);
      submitData.append('email', formData.email);
      submitData.append('phone', formData.phone);
      submitData.append('position', formData.position);
      submitData.append('experience', formData.experience);
      submitData.append('coverLetter', formData.coverLetter);
      submitData.append('resume', formData.resume);
      
      // Submit to backend
      const response = await axios.post(`${BACKEND_URL}/api/careers/submit`, submitData, {
        headers: {
          'Content-Type': 'multipart/form-data'
        }
      });
      
      if (response.data.success) {
        toast({
          title: "Application Submitted!",
          description: "Thank you for your interest. We'll review your application and contact you soon.",
        });
        
        // Reset form
        setFormData({
          name: '',
          email: '',
          phone: '',
          position: '',
          experience: '',
          coverLetter: '',
          resume: null
        });
        
        // Reset file input
        const fileInput = document.getElementById('resume');
        if (fileInput) fileInput.value = '';
      } else {
        toast({
          title: "Error",
          description: response.data.message || "Failed to submit application",
          variant: "destructive"
        });
      }
    } catch (error) {
      console.error('Error submitting application:', error);
      toast({
        title: "Error",
        description: "Failed to submit application. Please try again.",
        variant: "destructive"
      });
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleChange = (e) => {
    setFormData({
      ...formData,
      [e.target.name]: e.target.value
    });
  };

  const handleFileChange = (e) => {
    setFormData({
      ...formData,
      resume: e.target.files[0]
    });
  };

  return (
    <div className="min-h-screen">
      {/* Hero Section */}
      <section className="bg-gradient-to-r from-blue-900 to-blue-700 text-white py-20">
        <div className="container mx-auto px-4">
          <div className="max-w-4xl">
            <div className="inline-block bg-yellow-500 text-gray-900 px-6 py-3 rounded-lg text-lg font-bold mb-6 animate-pulse">
              🔥 NOW HIRING - Journeyperson 309A 🔥
            </div>
            <h1 className="text-4xl md:text-5xl font-bold mb-6">
              Join Our Team
            </h1>
            <p className="text-xl text-blue-100 mb-8">
              Build your career with Studerus Electric Inc. We're always looking for talented, dedicated electricians to join our growing team serving London, Kingston, and surrounding areas.
            </p>
            <div className="flex flex-wrap gap-4">
              <Button asChild size="lg" className="bg-yellow-500 hover:bg-yellow-600 text-gray-900 font-semibold">
                <a href="#apply">Apply Now</a>
              </Button>
              <Button asChild size="lg" variant="outline" className="border-2 border-white text-white hover:bg-white hover:text-blue-900">
                <a href="tel:519-709-9242">
                  <Phone className="mr-2 h-5 w-5" />
                  Call 519-709-9242
                </a>
              </Button>
            </div>
          </div>
        </div>
      </section>

      {/* Current Opening - Featured */}
      <section className="py-16 bg-yellow-50 border-y-4 border-yellow-500">
        <div className="container mx-auto px-4">
          <div className="max-w-4xl mx-auto">
            <div className="bg-white rounded-lg shadow-xl p-8 border-4 border-yellow-500">
              <div className="flex items-center gap-3 mb-4">
                <Award className="h-10 w-10 text-yellow-600" />
                <h2 className="text-3xl font-bold text-gray-900">NOW HIRING: Journeyperson Electrician 309A</h2>
              </div>
              
              <div className="space-y-4 text-gray-700">
                <div>
                  <h3 className="text-xl font-semibold text-gray-900 mb-2">Position Details:</h3>
                  <ul className="list-disc list-inside space-y-2 ml-4">
                    <li>Valid 309A Electrical License required</li>
                    <li>Minimum 3 years experience in residential and commercial electrical work</li>
                    <li>Knowledge of Ontario Electrical Safety Code</li>
                    <li>Strong troubleshooting and problem-solving skills</li>
                    <li>Excellent communication and customer service abilities</li>
                    <li>Valid driver's license and clean driving record</li>
                  </ul>
                </div>

                <div>
                  <h3 className="text-xl font-semibold text-gray-900 mb-2">What We Offer:</h3>
                  <ul className="list-disc list-inside space-y-2 ml-4">
                    <li>Competitive salary based on experience</li>
                    <li>Company vehicle and tools provided</li>
                    <li>Health benefits package</li>
                    <li>Ongoing training and professional development</li>
                    <li>Supportive team environment</li>
                    <li>Opportunities for advancement</li>
                  </ul>
                </div>

                <div className="bg-blue-50 p-4 rounded-lg mt-6">
                  <p className="text-lg font-semibold text-blue-900 text-center">
                    Ready to join our team? Apply below!
                  </p>
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Why Work With Us */}
      <section className="py-16 bg-white">
        <div className="container mx-auto px-4">
          <div className="text-center mb-12">
            <h2 className="text-3xl md:text-4xl font-bold text-gray-900 mb-4">
              Why Work With Studerus Electric?
            </h2>
            <p className="text-xl text-gray-600">
              Join a family-owned business that values its employees
            </p>
          </div>
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6">
            <Card>
              <CardHeader>
                <div className="w-16 h-16 bg-blue-900 text-white rounded-lg flex items-center justify-center mb-4">
                  <Users className="h-8 w-8" />
                </div>
                <CardTitle>Team Environment</CardTitle>
              </CardHeader>
              <CardContent>
                <p className="text-gray-600">
                  Work alongside experienced professionals in a supportive, collaborative atmosphere
                </p>
              </CardContent>
            </Card>

            <Card>
              <CardHeader>
                <div className="w-16 h-16 bg-blue-900 text-white rounded-lg flex items-center justify-center mb-4">
                  <TrendingUp className="h-8 w-8" />
                </div>
                <CardTitle>Career Growth</CardTitle>
              </CardHeader>
              <CardContent>
                <p className="text-gray-600">
                  Opportunities for advancement and continuous professional development
                </p>
              </CardContent>
            </Card>

            <Card>
              <CardHeader>
                <div className="w-16 h-16 bg-blue-900 text-white rounded-lg flex items-center justify-center mb-4">
                  <Award className="h-8 w-8" />
                </div>
                <CardTitle>Quality Work</CardTitle>
              </CardHeader>
              <CardContent>
                <p className="text-gray-600">
                  Take pride in delivering high-quality electrical services to satisfied customers
                </p>
              </CardContent>
            </Card>

            <Card>
              <CardHeader>
                <div className="w-16 h-16 bg-blue-900 text-white rounded-lg flex items-center justify-center mb-4">
                  <Briefcase className="h-8 w-8" />
                </div>
                <CardTitle>Competitive Benefits</CardTitle>
              </CardHeader>
              <CardContent>
                <p className="text-gray-600">
                  Competitive salary, company vehicle, tools, and comprehensive benefits package
                </p>
              </CardContent>
            </Card>
          </div>
        </div>
      </section>

      {/* Application Form */}
      <section id="apply" className="py-16 bg-gray-50">
        <div className="container mx-auto px-4">
          <div className="max-w-3xl mx-auto">
            <Card>
              <CardHeader>
                <CardTitle className="text-3xl text-center">Apply for a Position</CardTitle>
                <CardDescription className="text-center text-lg">
                  Complete the form below to submit your application
                </CardDescription>
              </CardHeader>
              <CardContent>
                <form onSubmit={handleSubmit} className="space-y-6">
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                    <div className="space-y-2">
                      <Label htmlFor="name">Full Name *</Label>
                      <Input
                        id="name"
                        name="name"
                        value={formData.name}
                        onChange={handleChange}
                        required
                        placeholder="John Doe"
                      />
                    </div>
                    <div className="space-y-2">
                      <Label htmlFor="email">Email *</Label>
                      <Input
                        id="email"
                        name="email"
                        type="email"
                        value={formData.email}
                        onChange={handleChange}
                        required
                        placeholder="john@example.com"
                      />
                    </div>
                  </div>

                  <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                    <div className="space-y-2">
                      <Label htmlFor="phone">Phone Number *</Label>
                      <Input
                        id="phone"
                        name="phone"
                        type="tel"
                        value={formData.phone}
                        onChange={handleChange}
                        required
                        placeholder="519-555-0123"
                      />
                    </div>
                    <div className="space-y-2">
                      <Label htmlFor="position">Position Applying For *</Label>
                      <select
                        id="position"
                        name="position"
                        value={formData.position}
                        onChange={handleChange}
                        required
                        className="w-full h-10 px-3 rounded-md border border-input bg-background"
                      >
                        <option value="">Select a position</option>
                        <option value="journeyperson-309a">Journeyperson Electrician 309A</option>
                        <option value="apprentice">Apprentice Electrician</option>
                        <option value="helper">Electrical Helper</option>
                        <option value="other">Other</option>
                      </select>
                    </div>
                  </div>

                  <div className="space-y-2">
                    <Label htmlFor="experience">Years of Experience</Label>
                    <Input
                      id="experience"
                      name="experience"
                      value={formData.experience}
                      onChange={handleChange}
                      placeholder="e.g., 5 years"
                    />
                  </div>

                  <div className="space-y-2">
                    <Label htmlFor="resume">Resume / CV *</Label>
                    <div className="border-2 border-dashed border-gray-300 rounded-lg p-6 text-center hover:border-blue-500 transition-colors">
                      <Upload className="h-12 w-12 text-gray-400 mx-auto mb-2" />
                      <Input
                        id="resume"
                        name="resume"
                        type="file"
                        onChange={handleFileChange}
                        required
                        accept=".pdf,.doc,.docx"
                        className="cursor-pointer"
                      />
                      <p className="text-sm text-gray-500 mt-2">
                        Accepted formats: PDF, DOC, DOCX (Max 5MB)
                      </p>
                      {formData.resume && (
                        <p className="text-sm text-green-600 mt-2 font-semibold">
                          Selected: {formData.resume.name}
                        </p>
                      )}
                    </div>
                  </div>

                  <div className="space-y-2">
                    <Label htmlFor="coverLetter">Cover Letter / Additional Information</Label>
                    <Textarea
                      id="coverLetter"
                      name="coverLetter"
                      value={formData.coverLetter}
                      onChange={handleChange}
                      placeholder="Tell us about yourself, your experience, and why you'd like to join our team..."
                      rows={6}
                    />
                  </div>

                  <div className="bg-blue-50 p-4 rounded-lg">
                    <p className="text-sm text-gray-700">
                      <strong>Note:</strong> By submitting this application, you consent to Studerus Electric Inc. 
                      reviewing your information and contacting you regarding employment opportunities. 
                      Your information will be sent to Info@studeruselectric.com.
                    </p>
                  </div>

                  <div className="flex gap-4">
                    <Button type="submit" size="lg" className="flex-1 bg-blue-900 hover:bg-blue-800" disabled={isSubmitting}>
                      {isSubmitting ? (
                        <>
                          <Loader2 className="mr-2 h-5 w-5 animate-spin" />
                          Submitting...
                        </>
                      ) : (
                        'Submit Application'
                      )}
                    </Button>
                    <Button
                      type="button"
                      size="lg"
                      variant="outline"
                      asChild
                    >
                      <a href="mailto:Info@studeruselectric.com">
                        <Mail className="mr-2 h-4 w-4" />
                        Email Directly
                      </a>
                    </Button>
                  </div>
                </form>
              </CardContent>
            </Card>

            <div className="mt-8 text-center">
              <p className="text-gray-600 mb-2">Questions about careers at Studerus Electric?</p>
              <div className="flex flex-wrap justify-center gap-4">
                <a href="tel:519-709-9242" className="text-blue-900 font-semibold hover:underline">
                  📞 Call 519-709-9242
                </a>
                <span className="text-gray-400">|</span>
                <a href="mailto:Info@studeruselectric.com" className="text-blue-900 font-semibold hover:underline">
                  ✉️ Info@studeruselectric.com
                </a>
              </div>
            </div>
          </div>
        </div>
      </section>
    </div>
  );
};

export default Careers;
